import usePageMeta from '../hooks/usePageMeta';
import { opt } from '../lib/media';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, Check, Zap } from 'lucide-react';
import { products as mockProducts, categoryMap, categories as mockCategories } from '../mock';
import { fetchProducts, fetchCategories } from '../api';
import ProductCard from '../components/ProductCard';

const sortOptions = [
  { id: 'featured', label: 'Featured' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
  { id: 'new', label: 'New Arrivals' },
];

export default function Shop() {
  const { category } = useParams();
  usePageMeta('Shop Designer Wear | Hitera', "Browse Hitera's designer wear collection — Indo-western, wedding, traditional and western couture, handcrafted in India.");
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').toLowerCase();
  const [sort, setSort] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [rtsOnly, setRtsOnly] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([fetchProducts(), fetchCategories()])
      .then(([p, c]) => {
        setAllProducts(Array.isArray(p) ? p : []);
        setCategories(Array.isArray(c) && c.length ? c : mockCategories);
      })
      .catch(() => { setAllProducts(mockProducts); setCategories(mockCategories); })
      .finally(() => setLoaded(true));
  }, []);

  const cat = category ? (categories.find((c) => c.slug === category) || categoryMap[category] || { name: category, tagline: '' }) : null;

  const list = useMemo(() => {
    let l = [...allProducts];
    if (category) l = l.filter((p) => p.category === category);
    if (q) l = l.filter((p) => p.name.toLowerCase().includes(q) || p.category.includes(q));
    if (rtsOnly) l = l.filter((p) => p.readyToShip);
    if (sort === 'price-asc') l.sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') l.sort((a, b) => b.price - a.price);
    else if (sort === 'new') l.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    return l;
  }, [category, q, sort, allProducts, rtsOnly]);

  const title = cat ? cat.name : q ? `Search: “${q}”` : 'All Creations';
  const bannerProduct = list[0] || allProducts.find((p) => !category || p.category === category);
  const banner = (bannerProduct && bannerProduct.images && bannerProduct.images[0]) || (cat && cat.image) || 'https://images.unsplash.com/photo-1756483509164-e9e652cb51bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';

  return (
    <div>
      {/* Category banner */}
      <div className="relative h-[38vh] md:h-[46vh] overflow-hidden bg-burgundy-950">
        {loaded && <img src={opt(banner, 1600)} alt={title} fetchPriority="high" decoding="async" width="1600" height="736" className="w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-burgundy-950/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-gold tracking-luxe text-xs uppercase mb-3 fade-up">HITÉRA · Collection</p>
          <h1 className="font-display text-5xl md:text-6xl text-cream fade-up">{title}</h1>
          {cat && <p className="text-cream/70 mt-3 text-sm fade-up">{cat.tagline}</p>}
        </div>
      </div>

      {/* Category pills */}
      <div className="border-b border-gold/15 bg-cream/40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex gap-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          <Link to="/shop" className={`whitespace-nowrap text-xs tracking-wideluxe uppercase px-4 py-2 border transition-colors ${!category ? 'bg-burgundy-900 text-cream border-burgundy-900' : 'border-burgundy-900/20 text-burgundy-900 hover:border-gold'}`}>All</Link>
          {categories.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} className={`whitespace-nowrap text-xs tracking-wideluxe uppercase px-4 py-2 border transition-colors ${category === c.slug ? 'bg-burgundy-900 text-cream border-burgundy-900' : 'border-burgundy-900/20 text-burgundy-900 hover:border-gold'}`}>{c.name}</Link>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <p className="text-sm text-burgundy-900/60 tracking-wide">{list.length} {list.length === 1 ? 'piece' : 'pieces'}</p>
          <button onClick={() => setRtsOnly((v) => !v)} data-testid="ready-to-ship-filter" className={`flex items-center gap-2 text-xs tracking-wideluxe uppercase px-4 py-2.5 border transition-colors ${rtsOnly ? 'bg-gold text-burgundy-950 border-gold' : 'border-burgundy-900/20 text-burgundy-900 hover:border-gold'}`}>
            <Zap size={14} className={rtsOnly ? 'fill-burgundy-950' : ''} /> Ready to Ship
          </button>
        </div>
        <div className="relative">
          <button onClick={() => setShowFilters((s) => !s)} className="flex items-center gap-2 text-xs tracking-wideluxe uppercase text-burgundy-900 border border-burgundy-900/20 px-4 py-2.5 hover:border-gold transition-colors">
            <SlidersHorizontal size={15} /> Sort: {sortOptions.find((o) => o.id === sort).label}
          </button>
          {showFilters && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-gold/20 shadow-xl z-20">
              {sortOptions.map((o) => (
                <button key={o.id} onClick={() => { setSort(o.id); setShowFilters(false); }} className="w-full flex items-center justify-between px-4 py-3 text-sm text-burgundy-900 hover:bg-cream transition-colors">
                  {o.label} {sort === o.id && <Check size={15} className="text-gold-dark" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 min-h-[40vh]">
        {!loaded ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx}>
                <div className="aspect-[3/4] skeleton" />
                <div className="h-4 skeleton mt-4 w-2/3 mx-auto" />
                <div className="h-3 skeleton mt-2 w-1/3 mx-auto" />
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-display text-3xl text-burgundy-900 mb-3">No pieces found</p>
            <p className="text-burgundy-900/60 text-sm mb-6">Explore our other collections.</p>
            <Link to="/shop" className="inline-block bg-burgundy-900 text-cream px-8 py-3 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">View All</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
            {list.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)}
          </div>
        )}
      </div>
    </div>
  );
}
